import React from 'react';
import { Link } from 'react-router-dom';
import classes from './DealerStockCard.module.scss';
import placeholderImage from '../../media/images/emptyFavorites.png';
import { buildVehicleName, buildVehicleSlug } from '../../helper/vehicleTitle';
import {
    formatMileage,
    formatTransmission,
    formatColor,
    saleStateFor,
} from '../../helper/dealerStockFilters';
import { galleryImagesForCar, priceForCar, formatUsd } from '../../services/dealerStock';
import DealerFavoriteButton from './DealerFavoriteButton';

/**
 * One vehicle in the dealer-stock grid.
 *
 * Split out of DealerStockContent so the grid and the detail page's
 * "Similar Vehicles" rail render the same card instead of drifting apart.
 *
 * The name comes from the shared buildVehicleName, which folds the chassis
 * code out of `modelDetail` and the trim out of `grade` into the title — so
 * this reads "1995 Nissan Skyline R33 GT-R V-Spec" exactly as the marketing
 * site does, rather than the bare "1995 NISSAN SKYLINE" it used to.
 */
const DealerStockCard = ({ car }) => {
    const detailUrl = `/dealer-stock/${buildVehicleSlug(car)}/${car._id}`;
    const heroImage = galleryImagesForCar(car)[0] || placeholderImage;
    const title = buildVehicleName(car);
    const year = car.year || 'Unknown';
    const { sold, badge } = saleStateFor(car);

    const transmission = formatTransmission(car);
    const color = formatColor(car);
    const blurb = car.grade || car.description || '';

    return (
        <div className={`${classes.card} ${sold ? classes.cardSold : ''}`}>
            {/* The heart sits over the image but OUTSIDE the link, so saving a
                car never also opens it. */}
            <div className={classes.media}>
            <Link to={detailUrl} className={classes.imageLink}>
                <div className={classes.imageContainer}>
                    <img
                        src={heroImage}
                        alt={`${year} ${title}`}
                        loading="lazy"
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = placeholderImage;
                        }}
                    />

                    {sold && (
                        <div className={classes.soldScrim}>
                            <span className={classes.soldStamp}>SOLD</span>
                        </div>
                    )}

                    {badge && (
                        <div className={`${classes.statusBadge} ${classes[`status${badge}`]}`}>
                            {badge}
                        </div>
                    )}

                    <div className={classes.priceBadge}>
                        {formatUsd(priceForCar(car))}
                    </div>

                    {car.mileage ? <div className={classes.kmBadge}>● {formatMileage(car)}</div> : null}
                </div>
            </Link>
            <DealerFavoriteButton car={car} className={classes.favoriteButton} />
            </div>

            <div className={classes.cardBody}>
                <Link to={detailUrl} className={classes.titleLink}>
                    {/* Two lines, not one: submodel titles ("... R32 GTS-t Type M")
                        need the second line, and a fixed min-height keeps the
                        View Details buttons aligned across a grid row. */}
                    <h3 className={classes.title}>{year} {title}</h3>
                </Link>

                {car.make && (
                    <div className={classes.taxonomy}>
                        {/* The marketing site links these to /makes/*, which this
                            portal has no equivalent of. Deep-link into the grid's
                            own filters instead — same intent, a route that exists. */}
                        <Link to={`/dealer-stock?make=${encodeURIComponent(car.make)}`}>{car.make}</Link>
                        {car.model && (
                            <>
                                <span className={classes.taxonomySeparator}>/</span>
                                <Link
                                    to={`/dealer-stock?make=${encodeURIComponent(car.make)}&model=${encodeURIComponent(car.model)}`}
                                >
                                    {car.model}
                                </Link>
                            </>
                        )}
                    </div>
                )}

                <div className={classes.detailsGrid}>
                    <div className={classes.detailItem}>
                        <i className="far fa-calendar" aria-hidden="true"></i>
                        <span>{car.year || 'N/A'}</span>
                    </div>
                    <div className={classes.detailItem}>
                        <i className="fas fa-cog" aria-hidden="true"></i>
                        <span>{transmission || 'N/A'}</span>
                    </div>
                    <div className={classes.detailItem}>
                        <i className="fas fa-road" aria-hidden="true"></i>
                        <span>{formatMileage(car)}</span>
                    </div>
                    <div className={classes.detailItem}>
                        <i className="fas fa-palette" aria-hidden="true"></i>
                        <span>{color || 'N/A'}</span>
                    </div>
                </div>

                <div className={classes.blurb}>
                    {car.source === 'current-inventory' && Array.isArray(car.features) && car.features.length > 0 ? (
                        <div className={classes.features}>
                            {/* Keyed by index — `features` is free-text and can
                                repeat within one vehicle. */}
                            {car.features.slice(0, 3).map((feature, i) => (
                                <span key={`${feature}-${i}`} className={classes.featurePill}>{feature}</span>
                            ))}
                            {car.features.length > 3 && (
                                <span className={classes.featureMore}>+{car.features.length - 3} more</span>
                            )}
                        </div>
                    ) : (
                        <p>{blurb || 'No description available'}</p>
                    )}
                </div>

                <Link to={detailUrl} className={classes.viewButton}>View Details</Link>
            </div>
        </div>
    );
};

export default DealerStockCard;
